import React, {useState} from 'react';
import {Box, Typography, Grid, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, GuidanceBanner, MarkerBar, BenchmarkLegend, BandChip, contextBandColor} from '../components/widgets.comp';
import {CONTEXT_BAND_COLORS} from '../../../utils/constants';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, benchmarkOf} from '../networkData';
import {isSuppressedItem, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortBars} from '../../DashboardCommon/barSort';

/* Slide 26 — Context & Support Item Profile (SND-26-01).

     SND-26-01  Item Scores   SND.S_8b.C_2   BarChartBMCard (items, Global avg.)

   Every item names its area (parent_label, parent_sublabel "CA1" …), so the
   grouping is the card's; items are numbered "Context Item 1 … N" in the
   order shown, straight through the areas (QA 9 Oct), whatever the card's own
   sublabel says. Global avg marker only. The deck's subtitle ("Which readiness items need
   national attention?") is a copy-paste leftover from the Readiness template;
   it is worded here for this page's topic. */
// An item under the n floor (suppressed) shows the placeholder in place of its bar, score and band.
const ItemRow = ({itemLabel, label, score, band, global, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.2, pl: 4}}>
        <Box sx={{width: {xs: 160, md: 220}, flexShrink: 0}}>
            {itemLabel && <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>{itemLabel}</Typography>}
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <MarkerBar score={score} color={contextBandColor(band)} global={global ?? undefined} height={12}/>
        <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY, width: 30, textAlign: 'right', flexShrink: 0}}>{score === null ? '—' : score}</Typography>
        <Box sx={{flexShrink: 0}}>{band && <BandChip label={band} color={contextBandColor(band)}/>}</Box>
        </>)}
    </Box>
);

const ListCard = ({title, items, color}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%'}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((it) => (
                    <Box component="li" key={it} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>{it}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const CONTEXT_ITEM_PROFILE_SECTION_CODE = 'S_8b';
const CARD_SECTIONS = [CONTEXT_ITEM_PROFILE_SECTION_CODE];

const ContextItemProfileTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const itemCard = findCard(dashboardData, CONTEXT_ITEM_PROFILE_SECTION_CODE, 'C_2');

    const ALL_ITEMS = (itemCard?.items || []).map((item) => {
        const a = String(item.parent_sublabel || '').match(/\d+/)?.[0];
        const suppressed = isSuppressedItem(item);
        return {
            code: item.code || item.label,
            areaNum: a ? Number(a) : null,
            areaTitle: a ? `Context Area ${a}` : '',
            parentLabel: item.parent_label || '',
            itemLabel: '',                       // numbered below, 1…N straight through the areas (QA 9 Oct)
            label: item.label,
            suppressed,
            score: suppressed ? null : whole(item.value),
            band: suppressed ? null : item.band,
            global: benchmarkOf(item, 'Global avg.'),
        };
    });
    const groups = new Map();
    ALL_ITEMS.forEach((it) => {
        const key = it.parentLabel || it.areaTitle;
        if (!groups.has(key)) groups.set(key, {num: it.areaNum, areaTitle: it.areaTitle, parentLabel: it.parentLabel, items: []});
        groups.get(key).items.push(it);
    });
    const GROUPED_AREAS = [...groups.values()].sort((a, b) => (a.num || 99) - (b.num || 99));
    // Items are numbered in the order shown, continuing across areas ("Context Item 1 … 8").
    let itemNo = 0;
    GROUPED_AREAS.forEach((g) => g.items.forEach((it) => { itemNo += 1; it.itemLabel = `Context Item ${itemNo}`; }));
    const shownGroups = sortBars(GROUPED_AREAS, sortMode);

    // Rankings run over the items that show a score; when none does, the lists say why.
    const SCORED = ALL_ITEMS.filter((it) => it.score !== null);
    const NONE_SHOWN = ALL_ITEMS.length > 0 && SCORED.length === 0 ? [SUPPRESSED_COPY] : [];
    const STRENGTHS = SCORED.length ? [...SCORED].sort((a, b) => b.score - a.score).slice(0, 3).map((it) => `${it.label} (${it.score})`) : NONE_SHOWN;
    const NEEDS = SCORED.length ? [...SCORED].sort((a, b) => a.score - b.score).slice(0, 3).map((it) => `${it.label} (${it.score})`) : NONE_SHOWN;
    const LOWEST = [...SCORED].sort((a, b) => a.score - b.score)[0];
    const guidanceText = LOWEST
        ? `"${LOWEST.label}" (${LOWEST.score}) is the lowest item. Start with one focused Area on it, then re-check the by-school view to target support where the gap is widest.`
        : null;

    return (
        <Box>
            <PageHeader
                title="Context & Support Item Profile"
                subtitle="Which context & support items need network attention?"
            />

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                                {itemCard?.title || 'Context & Support Item Scores'}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <BenchmarkLegend bandColors={CONTEXT_BAND_COLORS} showNational={false}/>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>
                        {shownGroups.map((areaGroup, idx) => (
                            <Box key={areaGroup.parentLabel || areaGroup.areaTitle || 'all'} sx={{mb: idx < shownGroups.length - 1 ? 3.5 : 0}}>
                                {(areaGroup.areaTitle || areaGroup.parentLabel) && <Box sx={{mb: 2}}>
                                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>
                                        {areaGroup.areaTitle}
                                    </Typography>
                                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>
                                        {areaGroup.parentLabel}
                                    </Typography>
                                </Box>}
                                {areaGroup.items.map((it) => (
                                    <ItemRow key={it.code} {...it} itemLabel={areaGroup.flattened && it.groupLabel ? `${it.itemLabel} · ${it.groupLabel}` : it.itemLabel}/>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ListCard title="Strengths" items={STRENGTHS} color="#1B6B55"/>
                    <Box sx={{mt: 2.5}}>
                        <ListCard title="Needs Attention" items={NEEDS} color="#E04535"/>
                    </Box>
                    {guidanceText && <GuidanceBanner label="Recommended action:" text={guidanceText}/>}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ContextItemProfileTab;
